import { AntDesign } from "@expo/vector-icons";
import { BlurView } from "expo-blur";
import { shareAsync } from "expo-sharing";
import React, { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { View, Modal, Pressable, Text, Platform, Image } from "react-native";
import ViewShot from "react-native-view-shot";

import i18n from "@/locales/i18n";
import { checkAsset } from "@/utils/transactions/ExchangeCurrencies";
import { formatToFloat } from "@/utils/transactions/FormatTransactionValue";
import { renderObjectValues } from "@/utils/transactions/RenderObject";
import {
  checkTransactionStatus,
  checkTransactionDate,
  propertiesToIgnore,
} from "@/utils/transactions/TransactionsUtils";

interface DepositModalProps {
  visible: boolean;
  details: any;
  onClick?: (state: boolean) => void;
  onClose: () => void;
}

export default function ReceiptModal(props: DepositModalProps) {
  const { t } = useTranslation();
  const ref = useRef<ViewShot>(null);

  const printToFile = useCallback(async () => {
    if (ref.current) {
      try {
        const uri = await ref.current.capture();
        console.log("Captured URI:", uri);
        await shareScreenshot(uri);
      } catch (error) {
        console.error("Error capturing or sharing image:", error);
      }
    }
  }, []);

  const shareScreenshot = async (uri: string) => {
    try {
      if (Platform.OS === "web") {
        const downloadLink = document.createElement("a");
        downloadLink.href = uri;
        downloadLink.target = "_blank";
        downloadLink.download = "receipt.jpg";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      } else {
        await shareAsync(uri);
      }
    } catch (error) {
      console.log("Error sharing screenshot:", error);
    }
  };

  const handleClose = () => {
    props.onClose();
  };

  return (
    <Modal visible={props.visible} transparent animationType="fade">
      <BlurView intensity={20} className="flex-1 justify-center items-center">
        <View className="p-6 bg-white1 border border-gray rounded-lg w-full h-full items-center lg:w-120 lg:h-168 shadow">
          <Pressable
            className="z-50 absolute right-3 top-3"
            onPress={handleClose}
          >
            <AntDesign name="close" size={24} color="black" />
          </Pressable>
          <ViewShot
            style={{ width: "100%", alignItems: "center", padding: 2 }}
            ref={ref}
            options={{ format: "jpg", quality: 1 }}
          >
            <View className="w-full h-fit items-center">
              <Image
                source={require("@/assets/logoreceipt.png")}
                style={{ height: 40, width: 160 }}
                resizeMode="contain"
              />
            </View>
            <View className="w-full items-center m-3">
              <Text className="text-black1 text-2xl text-center font-medium">
                {t("common:modal.details.title")}
              </Text>
            </View>
            <View className="w-full border-b border-midgray border-dashed items-center p-2">
              <View className="flex-row m-1 max-w-120 w-full justify-between">
                <Text className="text-sm font-thin">
                  {`${t("home/home:details.status")}:`}
                </Text>
                <Text className="text-sm">
                  {checkTransactionStatus(props.details, t)}
                </Text>
              </View>
              <View className="flex-row m-1 max-w-120 w-full justify-between">
                <Text className="text-sm font-thin">
                  {`${t("home/home:details.date")}:`}
                </Text>
                <Text className="text-sm">
                  {checkTransactionDate(props.details)}
                </Text>
              </View>
              {props.details.value && (
                <View className="flex-row m-1 max-w-120 w-full justify-between">
                  <Text className="text-sm font-thin">
                    {`${t("home/home:details.value")}:`}
                  </Text>
                  <Text className="text-sm">
                    {` ${
                      checkAsset(props.details.currentAsset) === "USD" ||
                      checkAsset(props.details.currentAsset) === "EUR"
                        ? new Intl.NumberFormat(i18n.language, {
                            style: "currency",
                            currency: "USD",
                          }).format(formatToFloat(props.details.value))
                        : props.details.value
                    }`}
                  </Text>
                </View>
              )}
              <View className="flex-row m-1 max-w-120 w-full justify-between">
                <Text className="text-sm font-thin">
                  {`${t("home/home:details.transactionid")}:`}
                </Text>
                <Text className="text-sm">
                  {` ${
                    (props.details?.categoryId ||
                      props.details?.layer2TransactionId ||
                      props.details?.id) ??
                    "-"
                  }`}
                </Text>
              </View>
              {(props.details.memo || props.details.railOriginatorMemo) && (
                <View className="flex-row m-1 max-w-120 w-full justify-between">
                  <Text className="text-sm font-thin">
                    {`${t("home/home:details.memo")}:`}
                  </Text>
                  <Text className="text-sm">
                    {` ${
                      (props.details.memo ||
                        props.details.railOriginatorMemo) ??
                      "-"
                    }`}
                  </Text>
                </View>
              )}
            </View>
            <View className="w-full border-b border-midgray border-dashed p-2">
              {renderObjectValues(props.details, propertiesToIgnore, t)}
            </View>
            <View className="w-full items-center">
              <Text className="text-midgray text-xs mt-1">
                ©2024 Izzi Wallet. All rights reserved.
              </Text>
            </View>
          </ViewShot>
          <View className="flex-grow w-full p-1 items-center justify-end">
            <Pressable
              className="flex-row items-center mt-2"
              onPress={printToFile}
            >
              <AntDesign name="download" size={16} color="#919191" />
              <Text className="mx-2 text-darkergray text-sm">
                {t("common:placeholder.download")}
              </Text>
            </Pressable>
          </View>
        </View>
      </BlurView>
    </Modal>
  );
}
